import { useState, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { TabView, TabPanel } from 'primereact/tabview';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

import { RootState } from '../../store';
import {
  useGetNewCounsellingQuery,
  useGetOverAllQuery,
} from './counselling.services';
import { exportToExcel } from '../../shared/export/excel';
import { useUI } from '../../shared/UI/Uicontext';
import { DIRECTORATE_LIST } from '../../shared/services/defaultValues';

/**
 * Migrated from Angular `ApplicationSummaryComponent`
 * (src/app/views/pages/counselling/application-summary).
 *
 * Route: /counselling/application-summary (same as Angular).
 */

// ── Column definitions ───────────────────────────────────────────────────────

interface Col {
  field: string;
  header: string;
}

const OVERALL_COLS: Col[] = [
  { field: 'Counselling', header: 'Counselling' },
  { field: 'directorateLabel', header: 'Directorate' },
  { field: 'Total', header: 'Total' },
  { field: 'Pending_Approval', header: 'Pending Approval' },
  { field: 'Hm Approved', header: 'HM Approved' },
  { field: 'Hm Rejected', header: 'HM Rejected' },
  { field: 'Beo Approved', header: 'BEO Approved' },
  { field: 'Beo Rejected', header: 'BEO Rejected' },
  { field: 'Deo Approved', header: 'DEO Approved' },
  { field: 'Deo Rejected', header: 'DEO Rejected' },
  { field: 'Ceo Approved', header: 'CEO Approved' },
  { field: 'Ceo Rejected', header: 'CEO Rejected' },
];

const DISTRICT_COLS: Col[] = [
  { field: 'District', header: 'District' },
  { field: 'EduDist', header: 'Education District' },
  { field: 'Block', header: 'Block' },
  { field: 'Counselling', header: 'Counselling' },
  { field: 'directorateLabel', header: 'Directorate' },
  { field: 'subjects', header: 'Subject' },
  { field: 'Total', header: 'Total' },
  { field: 'Pending Approval', header: 'Pending Approval' },
  { field: 'Hm Approved', header: 'HM Approved' },
  { field: 'Hm Rejected', header: 'HM Rejected' },
  { field: 'Beo Approved', header: 'BEO Approved' },
  { field: 'Beo Rejected', header: 'BEO Rejected' },
  { field: 'Deo Approved', header: 'DEO Approved' },
  { field: 'Deo Rejected', header: 'DEO Rejected' },
  { field: 'Ceo Approved', header: 'CEO Approved' },
  { field: 'Ceo Rejected', header: 'CEO Rejected' },
];

// ── Role-based filter logic (same as Angular) ────────────────────────────────

function filterByRole(
  data: any[],
  userTypeId: string,
  userTypeId1: string
): any[] {
  if (userTypeId === '5' && userTypeId1 === '29')
    return data.filter((r) => r.directorate === '4' || r.Directorate === '4');
  if (userTypeId === '5' && userTypeId1 === '6')
    return data.filter((r) => r.directorate === '5' || r.Directorate === '5');
  if (userTypeId === '5' && userTypeId1 === '8')
    return data.filter((r) => r.directorate === '6' || r.Directorate === '6');
  return data.filter((r) => (r.directorate ?? r.Directorate) !== '4');
}

function addDirectorateLabel(data: any[]): any[] {
  return data.map((row) => {
    const match = DIRECTORATE_LIST.find((d) => d.value === row.directorate);
    return {
      ...row,
      directorateLabel: match
        ? match.label
        : (row.directorate ?? row.Directorate),
    };
  });
}

// ── Component ────────────────────────────────────────────────────────────────

const ApplicationSummary = () => {
  const { usertypeid, emisUsertype1, loaded } = useSelector(
    (state: RootState) => state.session
  );

  const { showError } = useUI();

  // Tab state
  const [activeIndex, setActiveIndex] = useState(0);

  // Column chooser
  const [selectedOverallCols, setSelectedOverallCols] =
    useState<Col[]>(OVERALL_COLS);
  const [selectedDistrictCols, setSelectedDistrictCols] =
    useState<Col[]>(DISTRICT_COLS);

  // District tab counselling dropdown
  const [cnsnId, setCnsnId] = useState<string | null>(null);

  // Global filter
  const [globalFilterOverall, setGlobalFilterOverall] = useState('');
  const [globalFilterDistrict, setGlobalFilterDistrict] = useState('');

  // Table refs for column filters
  const dtOverall = useRef<DataTable<any[]>>(null);
  const dtDistrict = useRef<DataTable<any[]>>(null);

  // ── Data fetching ────────────────────────────────────────────────────────

  // Overall summary: flag=4, userId=usertypeid
  const { data: overallRaw, isFetching: overallLoading } = useGetOverAllQuery(
    { userId: usertypeid, flag: '4' },
    { skip: !loaded || activeIndex !== 0 }
  );

  // District summary: flag=1, userId=cnsnId (selected counselling)
  const { data: districtRaw, isFetching: districtLoading } = useGetOverAllQuery(
    { userId: cnsnId, flag: '1' },
    { skip: !loaded || activeIndex !== 1 || !cnsnId }
  );

  // Counselling list for the District tab dropdown
  const { data: counsellingRaw } = useGetNewCounsellingQuery(undefined, {
    skip: !loaded || usertypeid !== '5',
  });

  // ── Derived data ─────────────────────────────────────────────────────────

  const overAllSummary = useMemo(() => {
    if (!overallRaw?.dataStatus) return [];
    const filtered = filterByRole(overallRaw.result, usertypeid, emisUsertype1);
    return addDirectorateLabel(filtered);
  }, [overallRaw, usertypeid, emisUsertype1]);

  const districtWise = useMemo(() => {
    if (!districtRaw?.dataStatus) return [];
    const filtered = filterByRole(
      districtRaw.result,
      usertypeid,
      emisUsertype1
    );
    return addDirectorateLabel(filtered);
  }, [districtRaw, usertypeid, emisUsertype1]);

  const counsellingList = useMemo(() => {
    if (!counsellingRaw?.dataStatus) return [];
    const list = filterByRole(counsellingRaw.result, usertypeid, emisUsertype1);
    return list.map((item: any) => ({
      label: item.Title,
      value: item.IndexId,
    }));
  }, [counsellingRaw, usertypeid, emisUsertype1]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCounselling = useCallback(
    (value: string | null) => {
      setCnsnId(value);
      if (!value) {
        showError('Please select counselling type');
      }
    },
    [showError]
  );

  const handleExportExcel = useCallback(() => {
    const data = activeIndex === 0 ? overAllSummary : districtWise;
    const cols = activeIndex === 0 ? selectedOverallCols : selectedDistrictCols;

    if (!data.length) return;
    exportToExcel({
      data,
      columns: cols.map((c) => ({ key: c.field, label: c.header })),
      fileName: `Application Summary_export_${Date.now()}`,
      sheetName: 'Application Summary',
    });
  }, [
    activeIndex,
    overAllSummary,
    districtWise,
    selectedOverallCols,
    selectedDistrictCols,
  ]);

  // ── Shared table toolbar ─────────────────────────────────────────────────

  const renderToolbar = (
    allCols: Col[],
    selectedCols: Col[],
    setSelectedCols: (val: Col[]) => void,
    globalFilter: string,
    setGlobalFilter: (val: string) => void,
    dataLength: number
  ) => (
    <div className="row">
      <div className="col-md-3 col-12 mb-2">
        <MultiSelect
          value={selectedCols}
          options={allCols}
          optionLabel="header"
          filter
          filterBy="header"
          onChange={(e) => {
            // Restore original column order
            const selected = allCols.filter((col) => e.value.includes(col));
            setSelectedCols(selected);
          }}
          selectedItemsLabel="{0} columns selected"
          placeholder="Choose Columns"
          className="w-100"
        />
      </div>
      <div className="col-md-7 col-12 text-end">
        <span className="p-input-icon-left">
          <InputText
            type="text"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search keyword"
          />
        </span>
      </div>
      <div className="col-md-2 col-12 text-end">
        <Button
          type="button"
          label="Export As Excel"
          icon="pi pi-file-excel"
          className="p-button-success p-button-sm rounded"
          onClick={handleExportExcel}
          disabled={dataLength === 0}
        />
      </div>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        {/* Tab 1: Over All Summary */}
        <TabPanel header="Over All Summary">
          <div className="row Counselling-Secondary">
            <div className="col">
              <DataTable
                ref={dtOverall}
                value={overAllSummary}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                loading={overallLoading}
                showGridlines
                resizableColumns
                rowHover
                dataKey="CnsId"
                globalFilter={globalFilterOverall}
                globalFilterFields={selectedOverallCols.map((c) => c.field)}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                emptyMessage="No Records Found"
                tableStyle={{ minWidth: '60rem' }}
                header={renderToolbar(
                  OVERALL_COLS,
                  selectedOverallCols,
                  setSelectedOverallCols,
                  globalFilterOverall,
                  setGlobalFilterOverall,
                  overAllSummary.length
                )}
              >
                <Column
                  header="#"
                  body={(_data, options) => options.rowIndex + 1}
                  style={{ width: '3rem' }}
                />
                {selectedOverallCols.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable
                    filterPlaceholder="Search"
                    style={{ minWidth: '8rem' }}
                  />
                ))}
              </DataTable>
            </div>
          </div>
        </TabPanel>

        {/* Tab 2: District Wise Summary — visible only for usertypeid == '5' */}
        {usertypeid === '5' && (
          <TabPanel header="District Wise Summary">
            <div className="mb-3">
              <div style={{ maxWidth: '300px' }}>
                <label className="fw-bold d-block mb-1">Counselling Type</label>
                <Dropdown
                  value={cnsnId}
                  options={counsellingList}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select.."
                  filter
                  filterBy="label"
                  showClear
                  className="w-100"
                  onChange={(e) => handleCounselling(e.value)}
                />
              </div>
            </div>

            <div className="row Counselling-Secondary">
              <div className="col">
                <DataTable
                  ref={dtDistrict}
                  value={districtWise}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  loading={districtLoading}
                  showGridlines
                  resizableColumns
                  rowHover
                  dataKey="CnsId"
                  globalFilter={globalFilterDistrict}
                  globalFilterFields={selectedDistrictCols.map((c) => c.field)}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  emptyMessage="No Records Found"
                  tableStyle={{ minWidth: '80rem' }}
                  header={renderToolbar(
                    DISTRICT_COLS,
                    selectedDistrictCols,
                    setSelectedDistrictCols,
                    globalFilterDistrict,
                    setGlobalFilterDistrict,
                    districtWise.length
                  )}
                >
                  <Column
                    header="#"
                    body={(_data, options) => options.rowIndex + 1}
                    style={{ width: '3rem' }}
                  />
                  {selectedDistrictCols.map((col) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      sortable
                      // filter
                      filterPlaceholder="Search"
                      style={{ minWidth: '8rem' }}
                    />
                  ))}
                </DataTable>
              </div>
            </div>
          </TabPanel>
        )}
      </TabView>
    </div>
  );
};

export default ApplicationSummary;
